import React from 'react';
import styled from 'styled-components';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Label, Input, Select, FormGroup } from './ui';
import { theme } from '../styles/theme';

const BUTTON_TYPES = [
  { value: 'url',          labelKey: 'btnTypes.url',         labelDefault: 'URL (CTA link)' },
  { value: 'phone_number', labelKey: 'btnTypes.phone',       labelDefault: 'Telefone (CTA)' },
  { value: 'quick_reply',  labelKey: 'btnTypes.quickReply',  labelDefault: 'Resposta rápida' },
  { value: 'copy_code',    labelKey: 'btnTypes.copyCode',    labelDefault: 'Copiar código (cupom)' },
  { value: 'opt_out',      labelKey: 'btnTypes.optOut',      labelDefault: 'Opt-out / Cancelar (quick_reply)' },
];

export default function TemplateButtonsEditor({ buttons = [], onChange }) {
  const { t } = useTranslation('mktpl');

  const add = () => onChange([...buttons, { type: 'quick_reply', text: '' }]);

  const remove = (i) => onChange(buttons.filter((_, idx) => idx !== i));

  const update = (i, field, value) =>
    onChange(buttons.map((b, idx) => idx === i ? { ...b, [field]: value } : b));

  return (
    <Section>
      <SectionHeader>
        <SectionLabel>{t('buttonsSection')}</SectionLabel>
        {buttons.length < 3 && (
          <Button variant="secondary" size="sm" onClick={add}>
            <Plus size={12} /> {t('addButton')}
          </Button>
        )}
      </SectionHeader>

      {buttons.map((btn, i) => (
        <BtnRow key={i}>
          <FormGroup style={{ flex: '0 0 180px' }}>
            <Label>{t('btnType')}</Label>
            <Select value={btn.type} onChange={e => update(i, 'type', e.target.value)}>
              {BUTTON_TYPES.map(o => <option key={o.value} value={o.value}>{t(o.labelKey, { defaultValue: o.labelDefault })}</option>)}
            </Select>
          </FormGroup>

          {btn.type !== 'copy_code' && btn.type !== 'opt_out' && (
            <FormGroup style={{ flex: 1 }}>
              <Label>{t('btnText')}</Label>
              <Input value={btn.text || ''} onChange={e => update(i, 'text', e.target.value)} placeholder={t('btnTextPlaceholder')} maxLength={25} />
            </FormGroup>
          )}

          {btn.type === 'url' && (
            <>
              <FormGroup style={{ flex: 2 }}>
                <Label>{t('btnUrl')}</Label>
                <Input value={btn.url || ''} onChange={e => update(i, 'url', e.target.value)} placeholder="https://site.com/{{1}}" />
              </FormGroup>
              {btn.url?.includes('{{1}}') && (
                <FormGroup style={{ flex: 1 }}>
                  <Label>{t('btnUrlExample')}</Label>
                  <Input value={btn.urlExample || ''} onChange={e => update(i, 'urlExample', e.target.value)} placeholder="https://site.com/abc" />
                </FormGroup>
              )}
            </>
          )}

          {btn.type === 'phone_number' && (
            <FormGroup style={{ flex: 1 }}>
              <Label>{t('btnPhone')}</Label>
              <Input value={btn.phone || ''} onChange={e => update(i, 'phone', e.target.value)} placeholder="+5511999999999" />
            </FormGroup>
          )}

          {btn.type === 'copy_code' && (
            <FormGroup style={{ flex: 1 }}>
              <Label>{t('btnCouponExample')}</Label>
              <Input value={btn.example || ''} onChange={e => update(i, 'example', e.target.value)} placeholder="PROMO25" />
            </FormGroup>
          )}

          {btn.type === 'opt_out' && (
            <FormGroup style={{ flex: 1 }}>
              <Label>{t('btnOptOutText')}</Label>
              <Input value={btn.text || ''} onChange={e => update(i, 'text', e.target.value)} placeholder="Parar promoções" />
            </FormGroup>
          )}

          <RemoveBtn aria-label={t('remove', { ns: 'common' })} onClick={() => remove(i)} title={t('remove', { ns: 'common' })}>
            <Trash2 size={14} />
          </RemoveBtn>
        </BtnRow>
      ))}

      {buttons.length === 0 && (
        <Helper>{t('buttonsHint')}</Helper>
      )}
    </Section>
  );
}

const Section       = styled.div`margin-top: ${theme.spacing[3]}; padding-top: ${theme.spacing[3]}; border-top: 1px solid ${theme.colors.border};`;
const SectionHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: ${theme.spacing[2]};`;
const SectionLabel  = styled.div`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; text-transform: uppercase; letter-spacing: 0.05em;`;
const BtnRow        = styled.div`display: flex; gap: ${theme.spacing[2]}; align-items: flex-end; margin-bottom: ${theme.spacing[2]}; flex-wrap: wrap; padding: ${theme.spacing[2]}; background: ${theme.colors.gray50}; border-radius: ${theme.radii.md};`;
const RemoveBtn     = styled.button`background: none; border: none; cursor: pointer; color: ${theme.colors.danger}; padding: ${theme.spacing[1]}; align-self: flex-end; margin-bottom: 2px;`;
const Helper        = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: ${theme.spacing[1]};`;
